/**
 * Google Analytics 4 (gtag.js) 유틸
 * - 측정 ID는 환경변수 VITE_GA_TRACKING_ID (운영: shop3.io 스트림 / 개발: dev.shop3.io 스트림)
 * - SPA 라우트 전환 page_view 는 router.subscribe 에서 trackPageView 로 전송
 */
import config from '@/config/config';

declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

const TRACKING_ID: string | undefined = config.ga.tracking_id;

let initialized = false;

/** gtag.js 로드 및 초기화 (page_view 는 수동 전송) */
export function initAnalytics() {
    if (initialized || !TRACKING_ID) return;
    initialized = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    // SPA 라우팅과 중복되지 않도록 자동 page_view 비활성화
    window.gtag('config', TRACKING_ID, { send_page_view: false });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${TRACKING_ID}`;
    document.head.appendChild(script);
}

/**
 * 라우트 → 보고서용 한글 페이지명/콘텐츠 그룹 매핑
 * GA 보고서의 '페이지 제목' 차원에 아래 이름이 표시된다.
 */
function getPageMeta(path: string): { title: string; group: string } {
    const seg = path.split('/').filter(Boolean);

    if (path === '/' || path === '') return { title: 'shop3 메인 랜딩', group: '랜딩' };
    if (path === '/legacy' || path === '/mobile') return { title: 'shop3 구 랜딩', group: '랜딩' };
    if (path === '/try') return { title: '이벤트 체험 참여', group: '이벤트' };
    if (seg[0] === 'event') return { title: '이벤트 체험 결과', group: '이벤트' };
    if (path === '/promo') return { title: '구독 프로모션', group: '구독' };
    if (seg[0] === 'subscription') {
        if (seg[1] === 'checkout') return { title: '구독 결제', group: '구독' };
        if (seg[1] === 'success') return { title: '구독 완료', group: '구독' };
        return { title: '구독 안내', group: '구독' };
    }
    if (seg[0] === 'billing') return { title: '결제 처리', group: '구독' };
    if (seg[0] === 'account') return { title: '계정 설정', group: '기타' };
    if (seg[0] === 'social') return { title: '소셜 로그인', group: '기타' };

    // /:vendorPath/* — 벤더 상점 페이지
    if (seg.length >= 1) {
        const sub = seg[1];
        if (!sub) return { title: '상점 홈', group: '상점' };
        const subMap: Record<string, string> = {
            products: '상점 상품 상세',
            all: '상점 전체상품',
            new: '상점 신상품',
            'best-new': '상점 베스트',
            'today-new': '상점 오늘신상',
            discount: '상점 할인',
            'sold-out': '상점 품절',
            cart: '상점 장바구니',
            checkout: '상점 결제',
            'order-complete': '상점 주문완료',
            orders: '상점 주문내역',
            returns: '상점 반품',
            backorder: '상점 미송',
            sample: '상점 샘플',
            mypage: '상점 마이페이지',
            settings: '상점 설정',
            cash: '상점 캐시',
            deposit: '상점 예치금',
            'direct-order': '상점 바로주문',
            likes: '상점 찜',
            subscription: '상점 구독',
            'vendor-register': '판매자 가입',
        };
        return { title: subMap[sub] ?? `상점 ${sub}`, group: '상점' };
    }
    return { title: path, group: '기타' };
}

/** 라우트 전환 시 페이지뷰 전송 */
export function trackPageView(path: string) {
    if (!initialized || !TRACKING_ID) return;
    const meta = getPageMeta(path);
    window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: meta.title,
        content_group: meta.group,
    });
}

/** 커스텀 이벤트 전송 */
export function trackEvent(name: string, params?: Record<string, string | number | boolean>) {
    if (!initialized || !TRACKING_ID) return;
    window.gtag('event', name, params ?? {});
}
